import { inject, Injectable, isDevMode } from '@angular/core';

import { map, take, tap } from 'rxjs/operators';
import { validateUser } from './auth.helpers';
import { AuthState, AuthStore } from './auth.store';
import * as storage from './storage';

import * as Sentry from '@sentry/browser';

import { AnalyticsService } from '@23degrees/shared/services/analytics';
import { StripeService } from '@23degrees/shared/services/stripe';
import { AuthService } from '@23degrees/shared/services/auth';
import { GlobalService } from '@23degrees/shared/services/global';
import { Router } from '@angular/router';
import { NotificationsService } from 'angular2-notifications';
import { AuthQuery } from './auth.query';

import { ContentManager } from '@23degrees/shared/manager/content/content.manager';
import { DataManager } from '@23degrees/shared/manager/data/data.manager';
import { ContentLockService } from '@23degrees/shared/services/content/content-lock.service';
import { PaymentService2 } from '@23degrees/shared/services/payment/payment2.service';
import { firstValueFrom, forkJoin, of } from 'rxjs';
import { CollectionsStoreService } from '../collections/collections.service';
import { CollectionsStore } from '../collections/collections.store';
import { FavoritesStoreService } from '../favorites/favorites.service';
import { FavoritesStore } from '../favorites/favorites.store';
import { GoogleService } from '@23degrees/frontend/analytics/google/google.service';

/**
 * Using for changing state of the store
 */
@Injectable({ providedIn: 'root' })
export class AuthStoreService {
  private authStore = inject(AuthStore);
  private authQuery = inject(AuthQuery);
  private authDataService = inject(AuthService);
  private favoritesStoreService = inject(FavoritesStoreService);
  private collectionsStoreService = inject(CollectionsStoreService);
  private notificationService = inject(NotificationsService);
  private stripeService = inject(StripeService);
  private router = inject(Router);
  private analyticsService = inject(AnalyticsService);
  private contentManager = inject(ContentManager);
  private dataManager = inject(DataManager);
  private paymentService = inject(PaymentService2);
  protected collectionsStore = inject(CollectionsStore);
  protected favoritesStore = inject(FavoritesStore);
  private globalService = inject(GlobalService);
  private contentLockService = inject(ContentLockService);
  private googleService = inject(GoogleService);

  async login(creds, oauth = false) {
    // question this cen return stuff before setup user is done? cause it is async
    const auth  = oauth ? await this.authDataService.oauthLogin(creds).toPromise() : await this.authDataService.login(creds).toPromise();
    const { id, token } = auth;
    storage.saveAuth({ id, token });
    auth.user = validateUser(auth.user);
    this.authStore.update(auth);
    await this.setupUser();

    // console.log('>> login', auth);
    this.googleService.setUser('login', auth.user)
    

    return auth;
  }
  // TODO needs testing!
  async reloadUser() {
    await this.setupUser();
  }

  async loginConnector(creds) {
    const auth = await this.authDataService.connectorLogin(creds).toPromise();
    const { id, token } = auth;
    storage.saveAuth({ id, token });
    auth.user = validateUser(auth.user);
    this.authStore.update(auth);
    await this.setupUser();
    return auth;
  }

  setupUser = async () => {
    try {
      // console.log('[AuthStoreService] SetupUser');
      const { user, permissions } = await firstValueFrom(this.authDataService.getUserInfo());
      const products = await this.paymentService.getProductsByUser();
      const updateObject = {
        user: validateUser(user),
        permissions,
        products: products || [],
      };

      this.verifyUser(updateObject);
      Sentry.setUser({ id: user.slug, email: user.email, username: user.name });

      // console.log('>> auth', updateObject);
      this.googleService.setUser('auth', updateObject.user)

    } catch (error) {
      console.log('Error setting up user', error);

      if (!isDevMode()) {
        storage.clearEverything();
      }
    }
  };

  // @transaction()
  async logout() {
    const prevUser = await firstValueFrom(this.authDataService.getUserInfo());

    // console.log('[AuthStoreService] - logout');
    storage.clearAuth();
    // clear all cached content and data
    this.contentManager.emptyCache();
    this.dataManager.emptyCache();
    this.contentLockService.resetLocks();

    // setting anon user in storage and having a startstate
    // setup sweet anon user stuff
    const initialState = this.authStore.getInitialState();
    const { user, permissions } = await this.authDataService.getAnonUserInfo().toPromise();

    initialState.user = validateUser(user);
    initialState.permissions = permissions;
    // console.log('[AuthStoreService] logout', user.name);
    // this.collectionsStore.reset();
    // this.favoritesStore.reset();
    this.verifyUser(initialState);

    Sentry.setUser(null);
    // console.log('>> logout', initialState);
    this.googleService.setUser('logout', prevUser.user)

    // console.log("[AuthStoreService] Before Routing")
    this.router.navigate(['/']);
    // console.log("[AuthStoreService] After Routing")
  }

  signup(form, language, signupOptions: any = {}) {
    const cookieConsent = this.authQuery.checkConsent();
    return this.authDataService.signup(form, language, cookieConsent).pipe(
      map((auth: AuthState) => ({ ...auth, user: validateUser(auth.user) })),
      tap(async (auth) => {
        const { id, token } = auth;
        storage.saveAuth({ id, token });
        this.verifyUser(auth);

        // console.log('>> signup', auth);
        
        this.googleService.setUser('signup', auth.user)

        // register is done send to analytics
        // this.analyticsService.sendEvent('register', 'user', 'free', 0);
        // console.log('[signup]', 'Signup is done, start other stuff now mode : trial or subscribe ', { signupOptions });
        if (signupOptions?.cart && signupOptions.cart.length > 0) {
          await this.stripeService.startCheckoutSession(signupOptions);
        }
      }),
    );
  }

  async verifyUser(auth) {
    // console.log('[AuthStoreService] - verify user', auth);
    this.authStore.update(auth);
    this.authQuery.verified = true;

    if (!this.authQuery.isAnonUser()) {
      this.favoritesStoreService.get();
      await this.collectionsStoreService.get();
      this.collectionsStoreService.setActive(this.authQuery.user().defaultCollections.defaultCollectionId);
    }
  }
  /*
  carefull getUserBy id doesnt return same as get logged in user
  getUserById(id) {
    console.log("[AuthService] - getting UserByID");
    this.authDataService
      .getUserById(id)
      .pipe(
        take(1),
        tap(user => this.setUser(user))
      )
      .subscribe();
  }
  */

  setPermissions(permissions) {
    this.authStore.update({ permissions: permissions });
  }

  updateUserInfo(name: string, email: string, country: string, lang: string, notification?) {
    const userPreferences = this.authQuery.getValue().user.userPreferences;
    const newPreferences = { ...userPreferences, lang, country };

    return forkJoin([this.authDataService.updateUserInfo(name, email), this.authDataService.updatePreferences(newPreferences)]).pipe(
      tap(([user, preferences]) => {
        this.authStore.update((state) => {
          return {
            ...state,
            user: {
              ...state.user,
              name,
              email,
              userPreferences: {
                ...state.user.userPreferences,
                lang,
                country,
              },
            },
          };
        });

        if (notification) {
          this.notificationService.success(notification.title, notification.content, notification.override);
        }
      }),
    );
  }

  updatePreferences(newPreferences, notification?) {
    this.authQuery.preferences$.pipe(take(1)).subscribe((userPreferences) => {
      const preferences = { ...userPreferences, ...newPreferences };
      if (this.authQuery.isAnonUser()) {
        // console.log('[Update ANON Preferences]');
        this.authStore.update((state) => ({
          user: { ...state.user, userPreferences: preferences },
        }));
        return;
      }
      // console.log('[Updating Preferences on Server]');
      this.authDataService
        .updatePreferences(preferences)
        .pipe(
          tap(() => {
            this.authStore.update((state) => {
              let user: any = {
                ...state.user,
                userPreferences: {
                  ...state.user.userPreferences,
                  ...newPreferences,
                },
              };
              user = validateUser(JSON.parse(JSON.stringify(user)));
              return {
                ...state,
                user: user,
              };
            });
            if (notification) {
              this.notificationService.success(notification.title, notification.content, notification.override);
            }
          }),
          take(1),
        )
        .subscribe();
    });
  }

  addUserShortCuts(shortCut) {
    if (this.authQuery.isAnonUser()) return;

    this.authStore.update((state) => ({
      user: {
        ...state.user,
        userPreferences: {
          ...state.user.userPreferences,
          shortCuts: [...(state.user.userPreferences.shortCuts || []), shortCut],
        },
      },
    }));
  }

  updateUserShortCuts(shortCuts) {
    if (this.authQuery.isAnonUser()) return;

    this.authStore.update((state) => ({
      user: {
        ...state.user,
        userPreferences: {
          ...state.user.userPreferences,
          shortCuts,
        },
      },
    }));
  }

  updatePreferencesAndStyle(userPreferences, defaultStyles, notification?) {
    if (this.authQuery.isAnonUser()) {
      // console.log('[Update ANON PreferencesAndStyle]');
      this.authStore.update((state) => ({
        user: validateUser(
          JSON.parse(
            JSON.stringify({
              ...state.user,
              userPreferences: {
                ...state.user.userPreferences,
                currentStyle: userPreferences.currentStyle,
              },
              defaultStyles: defaultStyles,
            }),
          ),
        ),
      }));
      return;
    }
    this.authDataService
      .updatePreferencesAndStyle({
        userPreferences,
        defaultStyles,
      })
      .pipe(
        map((response) => response.data),
        tap(({ userPreferences, defaultStyles }) => {
          this.authStore.update((state) => ({
            user: {
              ...state.user,
              userPreferences: {
                ...state.user.userPreferences,
                currentStyle: userPreferences.currentStyle,
              },
              defaultStyles: defaultStyles,
            },
          }));
          if (notification) {
            this.notificationService.success(notification.title, notification.content, notification.override);
          }
        }),
        take(1),
      )
      .subscribe();
  }

  updateTemplateUserFilters(templateUserFilters) {
    this.updatePreferences({ templateUserFilters: templateUserFilters });
  }

  updatePageViewSettings(page, viewType) {
    this.authQuery.preferences$.pipe(take(1)).subscribe((userPreferences) => {
      const viewTypes = { ...userPreferences.viewTypes };
      viewTypes[page] = viewType;
      this.updatePreferences({ viewTypes });
    });
  }

  updateNewsletterConsent(consent) {
    this.authDataService
      .updateNewsletterConsent(consent)
      .pipe(
        map((response) => response.data),
        tap((newsletterConsent) => {
          this.authStore.update((state) => ({
            user: {
              ...state.user,
              newsletterConsent,
            },
          }));
          this.notificationService.success('Newsletter', 'Your settings were updated');
        }),
        take(1),
      )
      .subscribe();
  }

  deleteUserLogo() {
    this.authDataService
      .deleteLogo()
      .pipe(
        tap(() => {
          this.updatePreferences(
            { logo: null },
            {
              title: 'Success',
              content: 'Your image has been removed!',
            },
          );
        }),
        take(1),
      )
      .subscribe();
  }

  deleteProfilePicture() {
    this.authDataService
      .deleteProfilePicture()
      .pipe(
        tap(() => {
          this.updatePreferences(
            { profilePicture: null },
            {
              title: 'Success',
              content: 'Your image has been removed!',
            },
          );
        }),
        take(1),
      )
      .subscribe();
  }

  deleteDefaultContentLogo() {
    this.authDataService
      .deleteDefaultContentLogo()
      .pipe(
        tap(() => {
          this.updatePreferences(
            { defaultContentLogo: null },
            {
              title: 'Success',
              content: 'Your image has been removed!',
            },
          );
        }),
        take(1),
      )
      .subscribe();
  }

  // setUser(user) {
  //   this.authStore.update({ user: user });
  // }

  acceptConditions() {
    if (this.authQuery.isAnonUser()) {
      storage.setConsent(true);
      return of(true).pipe(take(1));
    }
    return this.authDataService.acceptConditions().pipe(
      take(1),
      tap((response: any) => {
        this.authStore.update((state) => ({
          user: {
            ...state.user,
            cookieConsent: response.data.data,
          },
        }));
      }),
    );
  }

  async refreshCustomer() {
    const { permissions } = await this.authDataService.getUserInfo().toPromise();
    const products = await this.paymentService.getProductsByUser();
    this.authStore.update({
      permissions,
      products: products || [],
    });
  }
}
